
import React, { useRef, useEffect, useState } from 'react';
import { Card, CardHeader, CardTitle, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from "@/components/ui/collapsible";
import { Users, Plus, Download, ChevronDown, Star } from "lucide-react";
import { useContext } from 'react';
import * as XLSX from 'xlsx';
import { toast } from "sonner";
import { User } from "@supabase/supabase-js";
import { ProjectContext } from "./ProjectStructureTab";
import ResourceFlowChart from "./resource-management/ResourceFlowChart";
import { getTotalTeamStats } from "./resource-assignment/utils/teamStatsUtils";
import ProjectInfoHeader from "./resource-assignment/ProjectInfoHeader";
import TasksList from "./resource-assignment/TasksList";
import ResourcesTable from "./resource-assignment/ResourcesTable";
import ResourceFormManager, { ResourceFormManagerRef } from "./resource-assignment/ResourceFormManager";
import { formatDate } from "./resource-assignment/utils/dateUtils";
import { useResourceAssignmentOptimized } from "./resource-assignment/hooks/useResourceAssignmentOptimized";
import { getAssignmentsForTask, getTaskDatesFromAssignments } from "./resource-assignment/utils/resourceUtils";
import { useUserRole } from "./hooks/useUserRole";

interface ResourceAssignmentTabProps {
  user: User | null;
}

const ResourceAssignmentTab = ({ user }: ResourceAssignmentTabProps) => {
  const { selectedProject } = useContext(ProjectContext);
  const formManagerRef = useRef<ResourceFormManagerRef>(null);
  
  // Role-based access control
  const { role } = useUserRole(user);
  const isRestrictedUser = role === "Team member" || role === "Team Lead";
  
  const {
    assignments,
    resources,
    projectTasks,
    selectedTaskId,
    setSelectedTaskId,
    uniqueTaskIds,
    isLoading,
    isDialogOpen,
    setIsDialogOpen,
    editingAssignment,
    setEditingAssignment,
    isOrgChartOpen,
    setIsOrgChartOpen,
    handleDeleteResource,
    handleSaveResource,
    fetchResourceAssignments,
    fetchTaskAssignments,
    userRole,
    canAddResource,
  } = useResourceAssignmentOptimized(selectedProject);

  // Disable Add Resource for Managers when ra_addresource__project_manager is FALSE.
  const isAddDisabled = isRestrictedUser || (userRole === 'Manager' && !canAddResource);

  // Enhanced event listener for AI Project Advisor data transfer
  useEffect(() => {
    const handleRefresh = () => {
      console.log('🔄 PLAN IMPLEMENTATION: ResourceAssignmentTab refreshing assignments...');
      fetchResourceAssignments();
    };

    const handleAIDataTransfer = (event: CustomEvent) => {
      console.log('📥 PLAN IMPLEMENTATION: Received resource data transfer from AI Project Advisor:', event.detail);
      
      const { taskAllocations, timestamp, source } = event.detail;
      
      if (taskAllocations && taskAllocations.length > 0) {
        console.log(`✅ PLAN SUCCESS: Processing ${taskAllocations.length} task allocations from AI Project Advisor`);
        
        // Refresh assignments for current task
        if (selectedTaskId && fetchTaskAssignments) {
          fetchTaskAssignments(selectedTaskId, true);
        }
        
        toast.success(`Resource data updated! Received ${taskAllocations.length} task allocations from AI analysis.`);
        
        // If no task is currently selected, select the first one
        if (!selectedTaskId && taskAllocations.length > 0) {
          setSelectedTaskId(taskAllocations[0].taskId || taskAllocations[0].taskName);
        }
      }
    };

    window.addEventListener('refreshResourceAssignments', handleRefresh);
    window.addEventListener('refreshResourceAssignments', handleAIDataTransfer);
    
    return () => {
      window.removeEventListener('refreshResourceAssignments', handleRefresh);
      window.removeEventListener('refreshResourceAssignments', handleAIDataTransfer);
    };
  }, [fetchResourceAssignments, fetchTaskAssignments, selectedTaskId, setSelectedTaskId]);

  const handleAddResource = () => {
    if (formManagerRef.current?.handleAddResource) {
      formManagerRef.current.handleAddResource();
    }
  };

  const handleEditResource = (assignment: any) => {
    if (formManagerRef.current?.handleEditResource) {
      formManagerRef.current.handleEditResource(assignment);
    }
  };

  const getTaskDates = (taskId: string) => {
    return getTaskDatesFromAssignments(taskId, assignments, projectTasks);
  };

  const getAssignmentsForOrgChart = () => {
    return assignments; // Already filtered by selected task
  };

  const handleDownloadResourceAssignments = () => {
    if (uniqueTaskIds.length === 0) {
      toast.error('No project tasks available for download');
      return;
    }

    try {
      const workbook = XLSX.utils.book_new();

      // For the optimized version, we'll download current task assignments
      if (selectedTaskId && assignments.length > 0) {
        const headers = [
          'SN', 'Name', 'Role Type', 'Role', 'Start Date', 'End Date', 
          'Duration', 'Allocation %', 'Rate ($/hr)', 'Hours'
        ];

        const taskData = [
          headers,
          ...assignments.map((assignment, idx) => [
            idx + 1,
            assignment.name || 'N/A',
            assignment.role_type ? assignment.role_type.replace('-', ' ') : 'N/A',
            assignment.role || 'N/A',
            formatDate(assignment.start_date),
            formatDate(assignment.end_date),
            `${assignment.duration} days`,
            `${assignment.allocated_percentage}%`,
            `$${assignment.rate}/hr`,
            assignment.allocation_hrs || 'N/A'
          ])
        ];

        const worksheet = XLSX.utils.aoa_to_sheet(taskData);
        const sheetName = `Task_${selectedTaskId}`.substring(0, 31);
        XLSX.utils.book_append_sheet(workbook, worksheet, sheetName);

        const fileName = `${selectedProject?.name || 'Project'}_${selectedTaskId}_Resource_Assignments_${new Date().toISOString().split('T')[0]}.xlsx`;
        XLSX.writeFile(workbook, fileName);
        toast.success('Resource assignments downloaded successfully');
      } else {
        toast.warning('No assignments available for the selected task');
      }
    } catch (error) {
      console.error('Error downloading resource assignments:', error);
      toast.error('Failed to download resource assignments');
    }
  };

  if (!selectedProject) {
    return (
      <div className="h-full flex items-center justify-center">
        <Card className="bg-secondary/50 backdrop-blur-lg border-2 border-primary">
          <CardContent className="p-8 text-center">
            <Users className="h-16 w-16 text-primary mx-auto mb-4" />
            <h3 className="text-xl font-semibold text-white mb-2">No Project Selected</h3>
            <p className="text-gray-300">Please select a project from the Project Planning tab to view resource assignments.</p>
          </CardContent>
        </Card>
      </div>
    );
  }

  const teamStats = getTotalTeamStats(assignments);

  return (
    <div className="h-full flex flex-col overflow-hidden">
      <ProjectInfoHeader selectedProject={selectedProject} />

      <div className="flex-grow flex gap-2 overflow-hidden">
        <TasksList
          uniqueTaskIds={uniqueTaskIds}
          selectedTaskId={selectedTaskId}
          onTaskSelect={(taskId: string) => {
            console.log('🎯 PLAN IMPLEMENTATION: Task selected, will auto-fetch assignments:', taskId);
            setSelectedTaskId(taskId);
          }}
          getAssignmentsForTask={() => assignments} // Return current task assignments
          isLoading={isLoading}
        />

        <Card className="flex-grow bg-secondary/50 backdrop-blur-lg border border-primary">
          <CardHeader className="flex flex-row items-center justify-between">
            <CardTitle className="text-white text-[17px] flex items-center gap-1">
              {selectedTaskId ? `Assigned resources for ${selectedTaskId} task` : 'Select a task'}
              {assignments.length > 0 && <span className="text-green-300 text-xs ml-2"></span>}
            </CardTitle>
            {selectedTaskId && (
              <div className="flex gap-2">
                <Button 
                  onClick={handleDownloadResourceAssignments} 
                  disabled={isRestrictedUser}
                  className={`${
                    isRestrictedUser 
                      ? "bg-gray-600 text-gray-400 cursor-not-allowed hover:bg-gray-600" 
                      : "bg-primary hover:bg-primary/90 text-black"
                  }`}
                >
                  <Download className="h-4 w-4 mr-2" />
                  Download
                </Button>
                <Button 
                  onClick={handleAddResource} 
                  disabled={isAddDisabled}
                  className={`${
                    isAddDisabled 
                      ? "bg-gray-600 text-gray-400 cursor-not-allowed hover:bg-gray-600" 
                      : "bg-primary hover:bg-primary/90 text-black"
                  }`}
                >
                  <Plus className="h-4 w-4 mr-2" />
                  Add Resource
                </Button>
              </div>
            )}
          </CardHeader>
          <CardContent className="flex-grow overflow-hidden">
            <div className="mb-4 text-[10px] text-white">
              Total Project Tasks: ({uniqueTaskIds.length}) | 
              Current Task Team: ({teamStats.totalTeamSize}) | 
              Directors: ({teamStats.totalDirectors}), 
              Managers: ({teamStats.totalManagers}), 
              Team Leads: ({teamStats.totalTeamLeads}), 
              Team Members: ({teamStats.totalTeamMembers})
              {assignments.length > 0 && <span className="text-green-300 ml-2">✅ Optimized Loading</span>}
            </div>

            {selectedTaskId ? (
              <div className="h-[450px] overflow-hidden">
                {assignments.length > 0 ? (
                  <ResourcesTable
                    assignments={assignments}
                    onEditResource={handleEditResource}
                    onDeleteResource={handleDeleteResource}
                    isRestrictedUser={isRestrictedUser || isAddDisabled}
                  />
                ) : isLoading ? (
                  <div className="text-center py-8">
                    <div className="animate-spin h-8 w-8 border-2 border-primary border-t-transparent rounded-full mx-auto mb-3"></div>
                    <p className="text-gray-300">Loading task assignments...</p>
                  </div>
                ) : (
                  <div className="text-center py-8">
                    <Users className="h-12 w-12 text-gray-400 mx-auto mb-3" />
                    <p className="text-gray-300">
                      No resources assigned to this task
                      <span className="block text-sm text-blue-300 mt-2">
                        💡 Tip: Run AI Project Advisor analysis to automatically populate resource assignments
                      </span>
                    </p>
                    <Button 
                      onClick={handleAddResource} 
                      disabled={isAddDisabled}
                      className={`mt-4 ${
                        isAddDisabled 
                          ? "bg-gray-600 text-gray-400 cursor-not-allowed hover:bg-gray-600" 
                          : "bg-primary hover:bg-primary/90 text-black"
                      }`}
                    >
                      <Plus className="h-4 w-4 mr-2" />
                      Add First Resource
                    </Button>
                  </div>
                )}
              </div>
            ) : (
              <div className="text-center py-12">
                <Star className="h-16 w-16 text-gray-400 mx-auto mb-4" />
                <p className="text-gray-300">
                  Select a task from the left to view assigned resources
                  <span className="block text-sm text-blue-300 mt-2">
                    💡 Tasks are loaded from project structure. Assignments are fetched per task to optimize performance.
                  </span>
                </p>
              </div>
            )}
          </CardContent>
        </Card>
      </div>

      {selectedProject && selectedTaskId && getAssignmentsForOrgChart().length > 0 && (
        <Collapsible open={isOrgChartOpen} onOpenChange={setIsOrgChartOpen}>
          <Card className="mt-2 bg-secondary/50 backdrop-blur-lg border-2 border-primary">
            <CollapsibleTrigger asChild>
              <CardHeader className="pb-3 cursor-pointer hover:bg-primary/5">
                <div className="flex justify-between items-center w-full">
                  <CardTitle className="text-white text-lg flex items-center gap-2">
                    Project Organization Chart - {selectedTaskId}
                    {assignments.length > 0 && <span className="text-green-300 text-sm">✅ OPTIMIZED</span>}
                  </CardTitle>
                  <div className="flex items-center gap-2">
                    <span className="text-xs text-primary-light">Click to expand or collapse</span>
                    <ChevronDown className={`h-4 w-4 transition-transform duration-200 text-primary-light ${isOrgChartOpen ? 'rotate-180' : ''}`} />
                  </div>
                </div>
              </CardHeader>
            </CollapsibleTrigger>
            <CollapsibleContent>
              <CardContent>
                <ResourceFlowChart 
                  resources={resources}
                  assignments={getAssignmentsForOrgChart()}
                  tasks={selectedProject.tasks || []}
                />
              </CardContent>
            </CollapsibleContent>
          </Card>
        </Collapsible>
      )}

      <ResourceFormManager
        ref={formManagerRef}
        isDialogOpen={isDialogOpen}
        setIsDialogOpen={setIsDialogOpen}
        editingAssignment={editingAssignment}
        setEditingAssignment={setEditingAssignment}
        resources={resources}
        selectedTaskId={selectedTaskId}
        getTaskDatesFromAssignments={getTaskDates}
        onSaveResource={handleSaveResource}
        existingAssignments={assignments}
      />
    </div>
  );
};

export default ResourceAssignmentTab;
